/**
 * User Filter Bar Component
 *
 * A filter bar with debounced name search and role dropdown.
 * Used by the Users list for server-side filtering.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box, TextField, FormControl, InputLabel, Select, MenuItem, Button } from '@material-ui/core';
import { userRoleLabels } from '../role/labels';

export interface UserFilter {
  name?: string;
  role?: string;
}

interface UserFilterBarProps {
  filter?: UserFilter;
  value?: UserFilter;
  onChange: (filter: UserFilter) => void;
}

const USER_ROLES = [
  { value: '', label: 'All Roles' },
  ...Object.entries(userRoleLabels).map(([value, label]) => ({
    value,
    label: String(label),
  })),
];

const UserFilterBar: React.FC<UserFilterBarProps> = ({ filter, value, onChange }) => {
  const effectiveFilter = (filter ?? value ?? {}) as UserFilter;

  // Local state for name input to support debounced updates
  const [nameInput, setNameInput] = useState<string>(effectiveFilter.name || '');
  const debounceTimeoutRef = useRef<number | undefined>(undefined);

  // Keep local input in sync when external filter changes (e.g., via URL)
  useEffect(() => {
    setNameInput(effectiveFilter.name || '');
  }, [effectiveFilter.name]);

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (debounceTimeoutRef.current !== undefined) {
        window.clearTimeout(debounceTimeoutRef.current);
      }
    },
    []
  );

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setNameInput(inputValue);

    // Clear any pending update
    if (debounceTimeoutRef.current !== undefined) {
      window.clearTimeout(debounceTimeoutRef.current);
    }

    // Debounce updates to avoid spamming the server while typing
    debounceTimeoutRef.current = window.setTimeout(() => {
      const trimmed = inputValue.trim();
      const nextFilter: UserFilter = { ...effectiveFilter };

      if (trimmed.length >= 2) {
        nextFilter.name = trimmed;
      } else {
        delete nextFilter.name;
      }

      onChange(nextFilter);
    }, 300);
  };

  const handleRoleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const role = event.target.value as string;
    const nextFilter: UserFilter = { ...effectiveFilter };

    if (role) {
      nextFilter.role = role;
    } else {
      delete nextFilter.role;
    }

    onChange(nextFilter);
  };

  const handleClearAll = () => {
    setNameInput('');
    onChange({});
  };

  const hasActiveFilters = Boolean(effectiveFilter.name || effectiveFilter.role);

  return (
    <Box display="flex" flexWrap="wrap" alignItems="center" gridGap={16} mb={1}>
      <TextField
        label="Name"
        variant="outlined"
        size="small"
        value={nameInput}
        onChange={handleNameChange}
        placeholder="Search by name..."
        style={{ minWidth: 200 }}
      />
      <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
        <InputLabel id="role-filter-label">Role</InputLabel>
        <Select labelId="role-filter-label" value={effectiveFilter.role || ''} onChange={handleRoleChange} label="Role">
          {USER_ROLES.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {hasActiveFilters && (
        <Button size="small" onClick={handleClearAll}>
          Clear All
        </Button>
      )}
    </Box>
  );
};

UserFilterBar.displayName = 'UserFilterBar';

export default UserFilterBar;
